import axios, { AxiosError } from "axios";
import { useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { TRADES_API_URL } from "../../../utils/env";
import { FilledTradesEndpointEnum } from "../../../enums/endpoints";

export interface IEpochReward {
  Account: string;
  Epoch: number;
  StakeAmount: string;
  StakeTier: string;
  VestingPeriod: number;
  StakingMultiplier: number;
  TradingVolume: number;
  TradingMultiplier: number;
  CombinedMultiplier: number;
  StakeValue: string;
  PercentageShare: number;
  RewardAmount: number;
  _CreatedTimestamp: number;
}

interface IEpochRewardsResponse {
  success: boolean;
  message: string;
  data: IEpochReward[] | { data: IEpochReward[] };
}

export const useEpochRewards = () => {
  const { tokenId } = useContext(AuthContext);

  const fetchEpochRewards = async (): Promise<IEpochReward[] | undefined> => {
    if (!tokenId) {
      return undefined;
    }

    try {
      const response = await axios.get<IEpochRewardsResponse>(
        TRADES_API_URL(FilledTradesEndpointEnum.STAKE_HISTORY),
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${tokenId}`,
            "Access-Control-Allow-Origin": "*",
          },
        }
      );

      if (response.data.success && response.data.data) {
        // Handle different response structures
        if (Array.isArray(response.data.data)) {
          return response.data.data;
        }
        if (
          typeof response.data.data === "object" &&
          "data" in response.data.data
        ) {
          return (response.data.data as { data: IEpochReward[] }).data;
        }
      }

      return [];
    } catch (error) {
      // do nothing
      return undefined;
    }
  };

  const swr = useSWR<IEpochReward[] | undefined, AxiosError>(
    tokenId ? ["stake-history", tokenId] : null,
    fetchEpochRewards,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000, // Cache for 1 minute
      refreshInterval: 300000, // Refresh every 5 minutes
    }
  );

  return {
    ...swr,
  };
};
